import React, { useEffect, useMemo, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiRefreshCw,
  FiClock,
  FiCalendar,
  FiMapPin,
  FiUsers,
  FiVideo,
  FiCheck,
  FiX,
  FiHelpCircle,
} from "react-icons/fi";
import { useRoomCalendar } from "../../../../context/RoomCalendarContext";
import { UserContext } from "../../../../context/UserContext";
import {
  formatDate,
  formatTime,
  formatDuration,
  canJoinMeeting,
  getRSVPLabel,
} from "../../../../utils/calendarUtils";
import LoadingBar from "../../../ui/LoadingBar";
import "./CalendarUpcomingView.css";

// Animation variants
const listVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.25 } },
  hover: { y: -4, transition: { duration: 0.2 } },
};

const CalendarUpcomingView = ({
  roomId,
  events = [],
  onEventClick,
  eventTypes,
  onRefresh,
}) => {
  const { upcomingEvents, loading, fetchEvents } = useRoomCalendar();
  const { user } = useContext(UserContext);
  const currentUserId = user?.id || user?._id;

  useEffect(() => {
    if (roomId) {
      fetchEvents(roomId, { upcoming: true });
    }
  }, [roomId, fetchEvents]);

  // Group events by date
  const groupedEvents = useMemo(() => {
    const eventsToUse = events.length > 0 ? events : upcomingEvents;
    const groups = {};

    eventsToUse.forEach((event) => {
      const date = new Date(event.startDate);
      const dateKey = date.toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = {
          date,
          events: [],
        };
      }
      groups[dateKey].events.push(event);
    });

    // Sort by date
    return Object.values(groups).sort(
      (a, b) => a.date.getTime() - b.date.getTime()
    );
  }, [events, upcomingEvents]);

  // Get event type config
  const getEventConfig = (eventType) => {
    return (
      eventTypes?.[eventType] ||
      eventTypes?.other || { color: "#64748b", bg: "#f1f5f9" }
    );
  };

  // Format relative date
  const formatRelativeDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);

    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    if (diffDays < 7) return `In ${diffDays} days`;
    return formatDate(date);
  };

  // Get RSVP status for current user
  const getMyRSVP = (event) => {
    const participation = event.participants?.find(
      (p) => p.user._id === currentUserId || p.user === currentUserId
    );
    return participation?.rsvp || "pending";
  };

  if (loading && upcomingEvents.length === 0 && events.length === 0) {
    return <LoadingBar text="Loading upcoming events..." />;
  }

  const hasEvents = groupedEvents.length > 0;

  return (
    <div className="rcup-container">
      {/* Header */}
      <motion.div
        className="rcup-header"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="rcup-header-left">
          <FiClock className="rcup-header-icon" />
          <h3 className="rcup-title">Upcoming Events</h3>
          <span className="rcup-subtitle">Next 30 Days</span>
        </div>
        <motion.button
          className="rcup-refresh-btn"
          onClick={onRefresh}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={loading}
        >
          <FiRefreshCw className={loading ? "rcup-spinning" : ""} />
          <span>Refresh</span>
        </motion.button>
      </motion.div>

      {/* Content */}
      {!hasEvents ? (
        <motion.div
          className="rcup-empty"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="rcup-empty-icon">
            <FiCalendar />
          </div>
          <h4 className="rcup-empty-title">No Upcoming Events</h4>
          <p className="rcup-empty-text">
            There are no scheduled events in the next 30 days.
          </p>
        </motion.div>
      ) : (
        <motion.div
          className="rcup-list"
          variants={listVariants}
          initial="hidden"
          animate="visible"
        >
          {groupedEvents.map((group) => (
            <div key={group.date.toISOString()} className="rcup-date-group">
              <motion.div
                className="rcup-date-header"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <span className="rcup-date-label">
                  {formatRelativeDate(group.date)}
                </span>
                <span className="rcup-date-full">{formatDate(group.date)}</span>
              </motion.div>

              <div className="rcup-events-list">
                <AnimatePresence>
                  {group.events.map((event) => {
                    const config = getEventConfig(event.eventType);
                    const myRSVP = getMyRSVP(event);
                    const joinStatus = canJoinMeeting(event);
                    const duration =
                      new Date(event.endDate) - new Date(event.startDate);

                    return (
                      <motion.div
                        key={event._id}
                        className="rcup-card"
                        style={{
                          "--event-color": config.color,
                          "--event-bg": config.bg,
                        }}
                        variants={cardVariants}
                        whileHover="hover"
                        onClick={() => onEventClick && onEventClick(event)}
                      >
                        {/* Color Accent */}
                        <div
                          className="rcup-card-accent"
                          style={{ background: config.color }}
                        />

                        {/* Card Content */}
                        <div className="rcup-card-content">
                          {/* Header Row */}
                          <div className="rcup-card-header">
                            <h4 className="rcup-card-title">{event.title}</h4>
                            <span
                              className="rcup-card-type"
                              style={{
                                background: config.bg,
                                color: config.color,
                              }}
                            >
                              {event.eventType}
                            </span>
                          </div>

                          {/* Time Info */}
                          <div className="rcup-card-time">
                            <div className="rcup-time-item">
                              <FiClock />
                              <span>
                                {formatTime(event.startDate)} -{" "}
                                {formatTime(event.endDate)}
                              </span>
                            </div>
                            <div className="rcup-time-item">
                              <span className="rcup-duration">
                                {formatDuration(duration)}
                              </span>
                            </div>
                          </div>

                          {/* Location */}
                          {event.location && (
                            <div className="rcup-card-location">
                              <FiMapPin />
                              <span>{event.location}</span>
                            </div>
                          )}

                          {/* Participants */}
                          <div className="rcup-card-participants">
                            <FiUsers />
                            <span>{event.acceptedCount || 0} attending</span>
                            {event.declinedCount > 0 && (
                              <span className="rcup-declined">
                                · {event.declinedCount} declined
                              </span>
                            )}
                          </div>

                          {/* Footer */}
                          <div className="rcup-card-footer">
                            {/* RSVP Status */}
                            <div className={`rcup-rsvp rcup-rsvp--${myRSVP}`}>
                              {myRSVP === "accepted" && <FiCheck />}
                              {myRSVP === "declined" && <FiX />}
                              {myRSVP === "tentative" && <FiHelpCircle />}
                              {myRSVP === "pending" && <FiClock />}
                              <span>{getRSVPLabel(myRSVP)}</span>
                            </div>

                            {/* Join Meeting Button */}
                            {event.meetingLinks &&
                              Object.keys(event.meetingLinks).length > 0 && (
                                <motion.button
                                  className={`rcup-join-btn ${
                                    joinStatus.canJoin
                                      ? "rcup-join-btn--active"
                                      : ""
                                  }`}
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (joinStatus.canJoin) {
                                      const url =
                                        event.meetingLinks.zoom?.url ||
                                        event.meetingLinks.googleMeet?.url ||
                                        event.meetingLinks.teams?.url;
                                      if (url) window.open(url, "_blank");
                                    }
                                  }}
                                  disabled={!joinStatus.canJoin}
                                  title={joinStatus.message}
                                >
                                  <FiVideo />
                                  <span>
                                    {joinStatus.canJoin
                                      ? "Join Now"
                                      : "Not Started"}
                                  </span>
                                </motion.button>
                              )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            </div>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default CalendarUpcomingView;
